import React from 'react';

interface AvatarProps {
    avatarUrl?: string | null;
    name?: string | null;
    email?: string | null;
    hasAvatar?: boolean | null;
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
    isLoading?: boolean;
    className?: string;
    onClick?: () => void;
    alt?: string;
    avatarColor?: string;
    fallback?: string;
}

const Avatar: React.FC<AvatarProps> = ({
    avatarUrl,
    name,
    email,
    hasAvatar,
    size = 'md',
    className = '',
    onClick,
    isLoading = false,
    alt = 'Avatar',
    avatarColor = '#3B82F6',
    fallback
}) => {
    // Get fallback character based on priority: name first char, then email first char, then '?'
    const getFallbackCharacter = (): string => {
        if (name && name.trim()) {
            return name.charAt(0).toUpperCase();
        }
        if (email && email.trim()) {
            return email.charAt(0).toUpperCase();
        }
        return '?';
    };

    // Size configurations
    const sizeClasses = {
        xs: 'w-6 h-6 text-xs leading-6 border',
        sm: 'w-8 h-8 text-sm leading-8 border-2',
        md: 'w-10 h-10 text-2xl leading-10 border-2',
        lg: 'w-16 h-16 text-4xl leading-16 border-4',
        xl: 'w-24 h-24 text-5xl leading-24 border-4',
        xxl: 'w-32 h-32 text-6xl leading-32 border-4'
    };

    const shouldShowImage = hasAvatar && avatarUrl;
    const fallbackCharacter = fallback !== undefined ? fallback : getFallbackCharacter();

    const baseClasses = `rounded-md overflow-hidden flex items-center justify-center ${sizeClasses[size]} ${isLoading ? 'animate-pulse bg-gray-300' : 'bg-white'}`;
    const combinedClasses = `${baseClasses} ${className}`;

    const handleClick = onClick ? () => onClick() : undefined;

    return (
        <div 
            className={combinedClasses}
            onClick={handleClick}
            style={{ 
                cursor: onClick ? 'pointer' : 'default',
                borderColor: avatarColor 
            }}
        >
            {shouldShowImage ? isLoading || (
                <img 
                    src={avatarUrl} 
                    alt={alt} 
                    className="w-full h-full object-cover"
                />
            ) : (
                <div className="text-black font-bold text-center select-none">
                    {isLoading || fallbackCharacter}
                </div>
            )}
        </div>
    );
};

export default Avatar;
